import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import RecipeFormatConfirmationModal from "./RecipeFormatConfirmationModal";
import { useCreateRecipe } from "@/hooks/mutations/useRecipeMutations";
import { useRecipeCloneOptions } from "@/hooks/useRecipes";
import { useDebounce } from "@/hooks/useDebounce";
import { AccordionSelect } from "@/components/ui/Select/AccordionSelect";

// Map frontend format IDs to backend recipeType enum values
const RECIPE_TYPE_MAP = {
  bakery: "Bakery",
  beverage: "Beverage",
  "beverage-psd": "Beverage PSD",
  confectionery: "Confectionary",
};

const RECIPE_TYPE_TO_FORMAT_ID = {
  Bakery: "bakery",
  Beverage: "beverage",
  "Beverage PSD": "beverage-psd",
  Confectionary: "confectionery",
};

const recipeFormats = [
  {
    id: "bakery",
    name: "Bakery",
    image: "/svg-icons/bakery.svg",
  },
  {
    id: "beverage",
    name: "Beverage",
    image: "/svg-icons/beverage.svg",
  },
  {
    id: "beverage-psd",
    name: "Beverage PSD",
    image: "/svg-icons/beveragePsd.svg",
  },
  {
    id: "confectionery",
    name: "Confectionery",
    image: "/svg-icons/confectionery.svg",
  },
];

export default function CreateRecipeModal({ isOpen, onClose, project, onConfirm, isIndependentRecipe = false, preSelectedFormat = null }) {
  const navigate = useNavigate();
  const [selectedFormat, setSelectedFormat] = useState(preSelectedFormat);
  const [selectedExistingRecipe, setSelectedExistingRecipe] = useState("");
  const [recipeSearchTerm, setRecipeSearchTerm] = useState("");
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const { mutateAsync: createRecipe } = useCreateRecipe();
  const debouncedRecipeSearchTerm = useDebounce(recipeSearchTerm, 300);
  const {
    data: cloneOptions = [],
    isLoading: isCloneOptionsLoading,
  } = useRecipeCloneOptions(debouncedRecipeSearchTerm, { enabled: isOpen });

  useEffect(() => {
    if (isOpen) {
      setSelectedFormat(preSelectedFormat);
    }
  }, [isOpen, preSelectedFormat]);

  if (!isOpen) return null;

  const handleConfirm = () => {
    if (selectedFormat || selectedExistingRecipe) {
      setShowConfirmationModal(true);
    }
  };

  const handleFinalConfirm = async () => {
    const projectId = project?._id || project?.id;
    if (!projectId && !isIndependentRecipe) return;

    const payload = selectedExistingRecipe
      ? {
          project: projectId || null,
          isIndependentRecipe: isIndependentRecipe ? true : undefined,
          copiedFromRecipe: selectedExistingRecipe,
        }
      : {
          recipeName: project?.projectName || project?.name || "New Independent Recipe",
          project: projectId || null,
          isIndependentRecipe: isIndependentRecipe ? true : undefined,
          recipeType: RECIPE_TYPE_MAP[selectedFormat] || "Bakery",
        };

    // Call the backend API to create the recipe
    const result = await createRecipe(payload);
    const createdRecipe = result?.data || result;
    const createdFormat =
      selectedFormat ||
      RECIPE_TYPE_TO_FORMAT_ID[createdRecipe?.recipeType] ||
      "bakery";

    onConfirm?.({
      format: createdFormat,
      existingRecipe: selectedExistingRecipe,
      project,
      recipe: createdRecipe,
    });

    // Navigate to ViewRecipePage with the new recipe ID
    const recipeId = createdRecipe?._id || createdRecipe?.id;
    const navPath = projectId 
       ? `/application-lab/application-recipes/${projectId}` 
       : `/application-lab/application-recipes/version/${recipeId}`;

    navigate(navPath, {
      state: {
        project: project || null,
        recipeId,
        format: createdFormat,
        isNewRecipe: true,
      },
    });

    handleClose();
  };

  const handleClose = () => {
    setSelectedFormat(preSelectedFormat);
    setSelectedExistingRecipe("");
    setRecipeSearchTerm("");
    setShowConfirmationModal(false);
    onClose();
  };

  const getSelectedFormatName = () => {
    if (selectedExistingRecipe) {
      return "Existing Recipe";
    }
    const format = recipeFormats.find(f => f.id === selectedFormat);
    return format ? format.name : "";
  };

  return (
    <div className="fixed inset-0 z-70 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="relative w-full max-w-[500px] sm:max-w-[580px] md:max-w-[660px] lg:max-w-[740px] xl:max-w-[800px] 2xl:max-w-[860px] mx-auto bg-gradient-to-b from-white to-[#E8E0F0] dark:from-gray-800 dark:to-gray-900 rounded-2xl md:rounded-3xl shadow-2xl p-6 sm:p-7 md:p-8 lg:p-9">
        {/* Header */}
        <div className="relative flex items-center justify-center pb-5 md:pb-6">
          <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 dark:text-white">
            Select Recipe Format
          </h2>
          <button
            onClick={handleClose}
            className="absolute -right-2 -top-2 sm:-right-3 sm:-top-3 p-1.5 sm:p-2 rounded-full bg-[#EEEBF4] hover:bg-purple-200 dark:bg-gray-700 dark:hover:bg-gray-600 transition-colors cursor-pointer"
          >
            <X className="w-4 h-4 sm:w-5 sm:h-5 text-primary dark:text-white" />
          </button>
        </div>

        {/* Content */}
        <div>
          {/* Recipe Format Cards */}
          <div className="grid grid-cols-2 gap-3 sm:gap-4 md:gap-5 mb-4 sm:mb-5 md:mb-6">
            {recipeFormats.map((format) => (
              <button
                key={format.id}
                onClick={() => {
                  setSelectedFormat(format.id);
                  setSelectedExistingRecipe("");
                }}
                className={cn(
                  "relative rounded-xl border-4 overflow-hidden transition-all duration-200 cursor-pointer",
                  "flex flex-col",
                  "hover:shadow-lg",
                  selectedFormat === format.id
                    ? "border-primary shadow-lg"
                    : "border-[#DCD4E7]"
                )}
              >
                {/* Image Container */}
                <div className="h-20 sm:h-24 md:h-28 lg:h-32 bg-white flex items-center justify-center p-2">
                  <img 
                    src={format.image} 
                    alt={format.name}
                    className="h-16 sm:h-20 md:h-24 lg:h-26 w-auto object-contain"
                  />
                </div>

                {/* Label */}
                <div className={cn(
                  "py-2 sm:py-2.5 px-3 sm:px-4 text-center transition-colors",
                  selectedFormat === format.id ? "bg-primary" : "bg-[#DCD4E7]"
                )}>
                  <span className={cn(
                    "font-bold text-xs sm:text-sm md:text-base transition-colors",
                    selectedFormat === format.id ? "text-white" : "text-gray-700"
                  )}>
                    {format.name}
                  </span>
                </div>
              </button>
            ))}
          </div>

          {/* Divider */}
          <div className="relative my-2 sm:my-3">
            <div className="relative flex justify-center">
              <span className="text-xs sm:text-sm md:text-base font-medium text-gray-400 dark:text-gray-500">
                Or
              </span>
            </div>
          </div>

          {/* Existing Recipe Dropdown */}
          <div className="mb-6 md:mb-8">
            <label className="block mb-2 sm:mb-3 text-xs sm:text-sm md:text-base font-semibold text-center text-gray-800 dark:text-gray-200">
              Choose from an Existing Recipe to Use as Base
            </label>
            <div className="relative w-full max-w-[420px] sm:max-w-[500px] md:max-w-[580px] mx-auto">
              <AccordionSelect
                id="existingRecipe"
                value={selectedExistingRecipe}
                onChange={(e) => {
                  setSelectedExistingRecipe(e.target.value);
                  setSelectedFormat(null);
                }}
                onSearchChange={(value) => setRecipeSearchTerm(value)}
                options={cloneOptions.map((recipe) => {
                  const legacyPart = recipe.legacyCode ? ` | Legacy: ${recipe.legacyCode}` : "";
                  return {
                    value: recipe._id,
                    label: `${recipe.name || "Untitled Recipe"} (${recipe.recipeCode || "No Code"}${legacyPart}) - v${recipe.version ?? 0}`,
                    searchText: `${recipe.name || ""} ${recipe.recipeCode || ""} ${recipe.legacyCode || ""}`,
                  };
                })}
                placeholder={isCloneOptionsLoading ? "Loading recipes..." : "Select recipe"}
                searchable
                disabled={isCloneOptionsLoading}
                maxHeight="max-h-[220px] md:max-h-[260px]"
                className="h-10 sm:h-11 rounded-lg border-primary/30 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs sm:text-sm px-3"
              />
            </div>
          </div>

          {/* Proceed Button */}
          <div className="flex justify-center">
            <button
              onClick={handleConfirm}
              disabled={!selectedFormat && !selectedExistingRecipe}
              className="px-8 sm:px-10 md:px-12 py-2.5 sm:py-3 rounded-lg text-white text-xs sm:text-sm md:text-base font-semibold bg-primary hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer shadow-md"
            >
              Proceed
            </button>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      <RecipeFormatConfirmationModal
        isOpen={showConfirmationModal}
        onClose={() => setShowConfirmationModal(false)}
        onConfirm={handleFinalConfirm}
        formatName={getSelectedFormatName()}
      />
    </div>
  );
}
